import { useCallback, useEffect, useRef, useState } from 'react';
import { PlusIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { zusToast } from '@zep/toast';
import { getClassGroupDetail as getClassroomDetail } from '@/api/gen/classroom/classroom.gen';
import { openClassroomShareModal } from '@/components/modals/ShareModal';
import { useViewport } from '@/hooks';
import { ProfileTitleWithMenus } from '@/layout/ProfileSidebarLayout';
import { ClassroomListSearchToolbar, type ClassroomListSortMode } from '@/pages/classrooms/components/ClassroomListSearchToolbar';
import { ClassroomListTab, ClassroomListTabs } from '@/pages/classrooms/components/ClassroomListTabs';
import type { ClassroomItem } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomCards';
import { CreateClassroomModal, type CreatedClassroom, CreatedClassroomModal, EditClassroomNameModal } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomCreateModals';
import { ClassroomListGridSkeleton } from '@/pages/classrooms/shared/components/ClassroomLoadingSkeletons';
import { CLASSROOM_ADD_STUDENT_ON_ENTER_KEY } from '@/pages/classrooms/shared/consts/classroomEntryFlags';
import { useDeleteClassroom } from '@/pages/classrooms/shared/hooks/useClassroomMutations';
import { useTrackOnce } from '@/pages/classrooms/shared/hooks/useTrackOnce';
import { CLASSROOM_ROUTES } from '@/shared/consts/routes';
import { usePointReleaseModal } from '@/shared/features/classroom/hooks';
import { trackClassroomCreateEvent, trackClassroomEvent } from '@/shared/features/classroom/utils';
import { useCanCreateClassroom } from '@/shared/hooks';
import { Button, confirmDialog } from '@/ui_v3';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
import { ClassroomCardsGrid } from './components/ClassroomCardsGrid';
import { ClassroomListEmptyState } from './components/ClassroomListEmptyState';
import { useClassroomItems } from './hooks/useClassroomItems';
import { useDashboardTabState } from './hooks/useDashboardTabState';
function getVisibleItems({
  activeTab,
  ownedClassrooms,
  joinedClassrooms
}: {
  activeTab: ClassroomListTab;
  ownedClassrooms: ClassroomItem[];
  joinedClassrooms: ClassroomItem[];
}) {
  if (activeTab === ClassroomListTab.OWNED_CLASSROOMS) {
    return ownedClassrooms;
  }
  return joinedClassrooms;
}
function getErrorStatus(error: unknown) {
  if (error && typeof error === 'object' && 'status' in error) {
    return String(error.status);
  }
  return undefined;
}
function ClassroomListHeader() {
  const {
    t,
    i18n
  } = useTranslation(['classroom']);
  return <>
      <div className="relative">
        <ProfileTitleWithMenus title={<h1 className="text-heading-xl-extrabold text-default">
              {formatEnglishTitleCase(t('classroom:classroomList.pageTitle'), i18n.resolvedLanguage)}
            </h1>} className="min-w-0 py-xs" hideMobileDropdown data-sentry-element="ProfileTitleWithMenus" data-sentry-source-file="ClassroomListPageView.tsx" />
      </div>
    </>;
}
export function ClassroomListPageView() {
  const {
    t
  } = useTranslation(['classroom']);
  const router = useRouter();
  const hasOpenedCreateFromQueryRef = useRef(false);
  const {
    isMobile
  } = useViewport();
  // 클래스를 가진 선생님이 랜딩하는 곳 → 포인트 출시 안내 자동 노출(계정당 1회).
  usePointReleaseModal();
  const deleteClassroom = useDeleteClassroom();
  const shouldShowOwnedClassroomsFirst = useCanCreateClassroom();
  // 학생 계정은 클래스 owner 여도 '만든 클래스' 탭과 owner 카드를 보여주지 않는다.
  // 학생이 만든 클래스는 제품 persona 밖 예외라, 계속 노출하면 학생관리/포인트 권한 분기가 꼬인다.
  const showOwnedTab = shouldShowOwnedClassroomsFirst;
  const {
    activeTab,
    tabs,
    handleChangeTab
  } = useDashboardTabState(showOwnedTab, shouldShowOwnedClassroomsFirst);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortMode, setSortMode] = useState<ClassroomListSortMode>('latest');
  const {
    ownedClassrooms,
    joinedClassrooms,
    isLoading,
    isFetching
  } = useClassroomItems({
    activeTab,
    searchText,
    sortMode
  });
  const visibleItems = getVisibleItems({
    activeTab,
    ownedClassrooms,
    joinedClassrooms
  });
  useTrackOnce(!isLoading, () => trackClassroomEvent('view_classroom_list', {
    source: 'classroom_list',
    activeTab,
    ownedCount: ownedClassrooms.length,
    joinedCount: joinedClassrooms.length,
    canCreateClassroom: shouldShowOwnedClassroomsFirst
  }));
  const handleShareClassroom = async (item: ClassroomItem) => {
    trackClassroomEvent('click_classroom_invite', {
      source: 'classroom_list',
      classroomId: item.id
    });
    try {
      const {
        data
      } = await getClassroomDetail(item.id);
      openClassroomShareModal({
        classroom: {
          id: data.classGroupId,
          code: data.classCode,
          name: data.name
        }
      });
    } catch {
      confirmDialog.open({
        header: {
          title: t('classroom:classroomList.errorTitle')
        },
        body: t('classroom:classroomList.shareLoadError'),
        confirmText: t('classroom:classroomList.confirm')
      });
    }
  };
  const openEditClassroomNameModal = (item: ClassroomItem) => {
    overlay.open(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <EditClassroomNameModal classroomId={item.id} name={item.name} onSubmitTrack={name => trackClassroomEvent('submit_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id,
        nameLength: name.length
      })} onSuccessTrack={() => trackClassroomEvent('success_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id
      })} onErrorTrack={error => trackClassroomEvent('fail_update_classroom_name', {
        source: 'rename_classroom_modal',
        classroomId: item.id,
        errorStatus: getErrorStatus(error)
      })} onClose={close} />;
    });
  };
  const handleDeleteClassroom = async (item: ClassroomItem) => {
    trackClassroomEvent('click_delete_classroom', {
      source: 'classroom_list',
      classroomId: item.id
    });
    const confirmed = await confirmDialog.openAsync({
      header: {
        title: t('classroom:classroomList.deleteDialog.title')
      },
      body: t('classroom:classroomList.deleteDialog.body', {
        name: item.name
      }),
      confirmText: t('classroom:classroomList.deleteDialog.confirm'),
      confirmIntent: 'destructive',
      cancelText: t('classroom:classroomList.deleteDialog.cancel')
    });
    if (!confirmed) return;
    try {
      await deleteClassroom.mutateAsync(item.id);
      trackClassroomEvent('success_delete_classroom', {
        source: 'classroom_list',
        classroomId: item.id
      });
    } catch {
      trackClassroomEvent('fail_delete_classroom', {
        source: 'classroom_list',
        classroomId: item.id
      });
      zusToast.error(t('classroom:classroomList.deleteError'));
    }
  };
  const openCreateClassroomModal = useCallback(async () => {
    trackClassroomEvent('click_create_classroom', {
      source: 'classroom_list',
      activeTab
    });
    trackClassroomEvent('view_create_classroom_modal', {
      source: 'create_classroom_modal',
      activeTab
    });
    const created = await overlay.openAsync<CreatedClassroom | undefined>(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <CreateClassroomModal onClose={() => close(undefined)} onCreated={data => close(data)} onSubmitTrack={name => trackClassroomCreateEvent('submit_create_classroom', {
        source: 'create_classroom_modal',
        nameLength: name.length
      })} onSuccessTrack={data => trackClassroomCreateEvent('success_create_classroom', {
        source: 'create_classroom_modal',
        classroomId: data.classroomId,
        memberCount: data.memberCount
      })} onErrorTrack={error => trackClassroomCreateEvent('fail_create_classroom', {
        source: 'create_classroom_modal',
        errorStatus: getErrorStatus(error)
      })} />;
    });
    if (!created) return;
    trackClassroomEvent('view_created_classroom_modal', {
      source: 'created_classroom_modal',
      classroomId: created.classroomId
    });
    const classroomId = await overlay.openAsync<string | undefined>(({
      close,
      isOpen
    }) => {
      if (!isOpen) return null;
      return <CreatedClassroomModal onClose={() => close(undefined)} {...created} onGoToClass={id => {
        trackClassroomEvent('click_enter_created_classroom', {
          source: 'created_classroom_modal',
          classroomId: id
        });
        close(id);
      }} />;
    });
    if (classroomId) {
      // 생성 직후 학생 탭에서 '학생 추가' 모달을 띄우도록 표식만 남긴다(학생 탭이 집어 간다).
      try {
        sessionStorage.setItem(CLASSROOM_ADD_STUDENT_ON_ENTER_KEY, classroomId);
      } catch {
        // 스토리지가 막힌 환경이면 모달만 안 뜬다 — 이동은 그대로 한다.
      }
      void router.push(CLASSROOM_ROUTES.detail(classroomId, true, {
        isMobile
      }));
    }
  }, [activeTab, isMobile, router]);
  useEffect(() => {
    if (router.query.openCreate !== 'true' || hasOpenedCreateFromQueryRef.current) return;
    hasOpenedCreateFromQueryRef.current = true;
    void router.replace(CLASSROOM_ROUTES.list(), undefined, {
      shallow: true
    });
    void openCreateClassroomModal();
  }, [openCreateClassroomModal, router]); // CTA 딥링크에서만 열며, query를 먼저 지워 재진입을 막는다.

  const handleEnterClassroom = () => {
    void router.push('/join');
  };
  const isEmpty = !isLoading && visibleItems.length === 0 && !searchText;
  // 만든 클래스 탭이 노출되는 경우(교사/비학생 owner)에만 탭 UI 를 보인다.
  const shouldShowClassroomTabs = showOwnedTab;
  const shouldShowCreateClassroomPrompt = shouldShowOwnedClassroomsFirst && activeTab === ClassroomListTab.OWNED_CLASSROOMS;
  return <div className="flex w-full flex-col gap-sm mobile-lg:gap-md" data-sentry-component="ClassroomListPageView" data-sentry-source-file="ClassroomListPageView.tsx">
      <ClassroomListHeader data-sentry-element="ClassroomListHeader" data-sentry-source-file="ClassroomListPageView.tsx" />

      <ClassroomListSearchToolbar leading={shouldShowClassroomTabs ? <ClassroomListTabs activeTab={activeTab} tabs={tabs} onChangeTab={handleChangeTab} /> : undefined} filteredItemCount={visibleItems.length} isFetchingSortOrder={isFetching} searchText={searchInput} sortMode={sortMode} compact={isMobile} onChangeSortMode={setSortMode} onSearchTextChange={setSearchInput} onSearchSubmit={value => setSearchText(value.trim())} data-sentry-element="ClassroomListSearchToolbar" data-sentry-source-file="ClassroomListPageView.tsx" />

      {isLoading ? <ClassroomListGridSkeleton /> : isEmpty ? activeTab === ClassroomListTab.OWNED_CLASSROOMS && shouldShowOwnedClassroomsFirst ? <ClassroomListEmptyState activeTab={activeTab} onCreateClassroom={openCreateClassroomModal} /> : <ClassroomListEmptyState activeTab={activeTab} /> : visibleItems.length === 0 && searchText ? <div className="flex flex-1 flex-col items-center justify-center gap-md py-2xl">
          <div className="flex flex-col items-center justify-center gap-xs">
            <img className="aspect-[3/2] h-[128px] w-[192px] shrink-0" src="/assets/empty_avatar.webp" alt="" />
            <span className="text-heading-md-bold text-default">
              &quot;{searchText}&quot;
            </span>
            <span className="text-heading-md-bold text-alternative">
              {t('classroom:classroomList.noSearchResult')}
            </span>
            {shouldShowCreateClassroomPrompt ? <span className="text-body-lg-medium text-assistive">
                {t('classroom:classroomList.noSearchResultSuggestion')}
              </span> : null}
          </div>
          {shouldShowCreateClassroomPrompt ? <Button intent="alternative" variant="outlined" size="lg" rounded="full" leftSlot={<PlusIcon size="md" />} onClick={openCreateClassroomModal}>
              {t('classroom:classroomList.createClassroom')}
            </Button> : null}
        </div> : <ClassroomCardsGrid visibleItems={visibleItems} shouldShowCreateCard={activeTab === ClassroomListTab.OWNED_CLASSROOMS && shouldShowOwnedClassroomsFirst} shouldShowEnterCard={activeTab === ClassroomListTab.JOINED_CLASSROOMS} onCreateClassroom={openCreateClassroomModal} onEnterClassroom={handleEnterClassroom} onDeleteClassroom={item => void handleDeleteClassroom(item)} onEditClassroomName={openEditClassroomNameModal} onShareClassroom={item => void handleShareClassroom(item)} />}
    </div>;
}